import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  EndJourneyResponse,
  Question,
  EndJourneyResponses,
  RespondentInfo,
} from '../../../types/end-journey'
import { FeedbackRequestLimits } from '../../../types/api'
import { endJourneyApi, ApiResponse } from '../api/endJourneyApi'

interface EndJourneyState {
  sessionId: string
  user: {
    email: string
    peers: RespondentInfo[]
    reportees: RespondentInfo[]
    managers: RespondentInfo[]
    timezone: string
    company: string
    feedbackRequest?: FeedbackRequestLimits
  }
  intro: any
  outro: any
  currentQuestionIndex: number
  responses: EndJourneyResponses | null
  questions: Question[]
  isComplete: boolean
  isLoading: boolean
  error: string | null
}

const initialState: EndJourneyState = {
  sessionId: '',
  user: {
    email: '',
    peers: [],
    reportees: [],
    managers: [],
    timezone: '',
    company: '',
  },
  intro: null,
  outro: null,
  currentQuestionIndex: -1,
  responses: null,
  questions: [],
  isComplete: false,
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchAssessmentData = createAsyncThunk(
  'endJourney/fetchAssessmentData',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await endJourneyApi.getAssessmentData(userId)
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch assessment data')
    }
  }
)

export const fetchResponses = createAsyncThunk(
  'endJourney/fetchResponses',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await endJourneyApi.getResponses(userId)
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch responses')
    }
  }
)

export const submitResponse = createAsyncThunk(
  'endJourney/submitResponse',
  async (
    { userId, response }: { userId: string; response: EndJourneyResponse },
    { rejectWithValue }
  ) => {
    try {
      await endJourneyApi.submitResponse(userId, response)
      return response
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to submit response')
    }
  }
)

const endJourneySlice = createSlice({
  name: 'endJourney',
  initialState,
  reducers: {
    setResponse: (
      state,
      action: PayloadAction<{
        questionId: string
        isLastQuestion: boolean
        answer: string | string[]
        otherAnswer?: string
        leadershipValue?: string
      }>
    ) => {
      const {
        questionId,
        answer,
        otherAnswer,
        isLastQuestion,
        leadershipValue,
      } = action.payload

      if (!state.responses) {
        state.responses = {
          answers: [],
        }
      }

      const existingIndex = state.responses.answers.findIndex(
        (r) => r.questionId === questionId
      )

      if (existingIndex !== -1) {
        state.responses.answers[existingIndex] = {
          questionId,
          answer,
          otherAnswer,
          isLastQuestion,
          leadershipValue,
        }
      } else {
        state.responses.answers.push({
          questionId,
          answer,
          otherAnswer,
          isLastQuestion,
          leadershipValue,
        })
      }
    },
    nextQuestion: (state) => {
      if (state.currentQuestionIndex < state.questions.length - 1) {
        state.currentQuestionIndex += 1
      }
    },
    previousQuestion: (state) => {
      if (state.currentQuestionIndex > -1) {
        state.currentQuestionIndex -= 1
      }
    },
    goToQuestion: (state, action: PayloadAction<number>) => {
      if (action.payload >= -1 && action.payload < state.questions.length) {
        state.currentQuestionIndex = action.payload
      }
    },
    resetAssessment: () => initialState,
    completeAssessment: (state) => {
      state.isComplete = true
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Assessment Data
      .addCase(fetchAssessmentData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssessmentData.fulfilled, (state, action) => {
        state.isLoading = false
        state.intro = action.payload.intro
        state.outro = action.payload.outro
        state.questions = action.payload.questions
        state.user = {
          ...action.payload.user,
          company: action.payload.user.company || ''
        }
      })
      .addCase(fetchAssessmentData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Fetch Responses
      .addCase(fetchResponses.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchResponses.fulfilled, (state, action) => {
        state.isLoading = false
        const apiResponse = action.payload as ApiResponse
        const { answers = [], currentQuestion, sessionId } = apiResponse
        state.responses = {
          answers: (answers || []).map((response) => ({
            questionId: response.question,
            answer: (response.answers || []).map((a) => a.value),
            otherAnswer: response.otherAnswer,
            leadershipValue: response.leadershipValue,
          })),
          currentQuestion,
        }
        state.sessionId = sessionId
      })
      .addCase(fetchResponses.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Submit Response
      .addCase(submitResponse.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(submitResponse.fulfilled, (state, action) => {
        state.isLoading = false
        const { questionId, answer, otherAnswer, leadershipValue } =
          action.payload

        if (!state.responses) {
          state.responses = {
            answers: [],
          }
        }

        const existingIndex = state.responses.answers.findIndex(
          (r) => r.questionId === questionId
        )

        if (existingIndex !== -1) {
          state.responses.answers[existingIndex] = {
            questionId,
            answer,
            otherAnswer,
            leadershipValue,
          }
        } else {
          state.responses.answers.push({
            questionId,
            answer,
            otherAnswer,
            leadershipValue,
          })
        }
      })
      .addCase(submitResponse.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const {
  setResponse,
  nextQuestion,
  previousQuestion,
  goToQuestion,
  resetAssessment,
  completeAssessment,
} = endJourneySlice.actions

export default endJourneySlice.reducer
